import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import { styled, useTheme, ThemeProvider, createTheme } from '@mui/material/styles';
import { Box, Chip, Container, CssBaseline, Grid, Pagination, Stack, Typography, useMediaQuery } from '@mui/material';
import { height, letterSpacing } from '@mui/system';

import { useDispatch, useSelector } from 'store';
import { clearStore, getCategories, getPostList } from 'store/slices/postList';

import { Helmet } from 'react-helmet-async';

import {
    buildSchemaGraph,
    generateBreadcrumbSchema,
    generateCollectionPageSchema,
    renderMultipleStructuredData
} from 'utils/structuredData';
import { GEO_PAGE_DESCRIPTIONS } from 'static/geoPageInfo';
import PageNotFound from 'views/page-not-found';
import { buildBlogDetailPath, buildBlogListPath } from 'utils/blogUrl';

// ==============================|| POST LIST PAGE ||============================== //

const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'uat';

const contentTheme = createTheme({
    components: {
        MuiCssBaseline: {
            styleOverrides: `
                h3 {
                    color: #1C1C1C;
                    font-size: 1.875rem;
                    font-weight: 800;
                    line-height: 1.43;
                    margin-top: 0;
                    margin-bottom: 10px
                }

                p {
                    color: #1C1C1C;
                    font-size: 1.25rem;
                    font-weight: 400;
                    line-height: 1.43;
                    margin-top: 0;
                }

                iframe {
                    width: 1080px;
                    height: 607.5px;
                    display: block;
                    margin: 0 auto;
                }

                @media (max-width: 1200px) {
                    iframe {
                        width: 860px;
                        height: 472.5px;
                    }
                }

                @media (max-width: 900px) {
                    iframe {
                        width: 540px;
                        height: 303.75px;
                    }
                }

                @media (max-width: 959.95px) {
                    h3 {
                        font-size: 1.5rem;
                    }

                    p {
                        font-size: 1rem;
                    }
                }

                @media (max-width: 600px) {
                    iframe {
                        width: 320px;
                        height: 180px;
                    }
                }
            `
        }
    }
});

const PostListPage = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { slug, page } = useParams();
    const { categoryList, postList, postCount } = useSelector((state) => state.postList);

    // Initialize local state from route params so SSR render matches the expected page
    const [selectedCategory, setSelectedCategory] = useState(slug || 'all');
    const [currentPage, setCurrentPage] = useState(page ? Number(page) : 1);

    // Keep local state in sync when route params change (client navigation)
    useEffect(() => {
        setSelectedCategory(slug || 'all');
        setCurrentPage(page ? Number(page) : 1);
    }, [slug, page]);

    // Combined client-side data loading: Fetch categories if needed, then posts
    // Skip on server - data should already be loaded via loadData
    useEffect(() => {
        if (typeof window === 'undefined') return;

        const load = async () => {
            const normalizedSelectedCategory = selectedCategory ? String(selectedCategory).toLowerCase() : 'all';
            const isValidCurrentPage = Number.isInteger(currentPage) && currentPage > 0;

            if (!isValidCurrentPage) return;

            // Ensure categories are available; if we fetch them now, wait for the effect to re-run with updated state
            if (!categoryList || categoryList.length === 0) {
                await dispatch(getCategories({}));
                return;
            }

            const invalidSelectedCategory =
                normalizedSelectedCategory !== 'all' &&
                !categoryList.some((category) => normalizedSelectedCategory === category.name.toLowerCase());
            if (invalidSelectedCategory) return;

            let targetCategory = 0;
            if (normalizedSelectedCategory !== 'all') {
                const found = categoryList.find((category) => normalizedSelectedCategory === category.name.toLowerCase());
                if (found) {
                    targetCategory = found.id;
                } else if (isDevelopment) {
                    console.warn(`[PostList] Category not found for slug: ${normalizedSelectedCategory}`);
                }
            }

            const apiParam = { categoryId: targetCategory, page: currentPage };
            if (isDevelopment) {
                console.debug('[PostList] dispatch getPostList', apiParam);
            }
            await dispatch(getPostList(apiParam));
        };

        load();
    }, [dispatch, categoryList, selectedCategory, currentPage]);

    // For SSR: check if data exists in store (from loadData) even if useEffect hasn't run
    // On client: use the normal isLoaded check
    const isServer = typeof window === 'undefined';
    // On server, if data was loaded via loadData, it should be available
    // On client, check if data is loaded
    const isLoaded = Boolean(categoryList?.length && postList?.length);

    const normalizedSlug = slug ? String(slug).toLowerCase() : null;
    const pageNum = page ? Number(page) : 1;
    const isValidPage = Number.isInteger(pageNum) && pageNum > 0;
    const hasCategories = Array.isArray(categoryList) && categoryList.length > 0;
    const hasKnownPostCount = typeof postCount === 'number' && !Number.isNaN(postCount);
    const maxPage = hasKnownPostCount ? Math.max(1, Math.ceil(postCount / 10)) : null;
    const isPageOutOfRange =
        hasKnownPostCount && page != null && maxPage != null && pageNum > maxPage && Array.isArray(postList) && postList.length === 0;
    const isReservedDetailSlug = normalizedSlug === 'detail';
    const isValidSlug =
        !normalizedSlug ||
        normalizedSlug === 'all' ||
        (hasCategories && categoryList.some((category) => category?.name?.toLowerCase() === normalizedSlug));
    const invalidSlug = isReservedDetailSlug || Boolean(normalizedSlug && normalizedSlug !== 'all' && hasCategories && !isValidSlug);
    const shouldRenderNotFound = !isValidPage || invalidSlug || isPageOutOfRange;
    const canonicalListPath = buildBlogListPath(slug, page);
    const needsCanonicalRedirect = !shouldRenderNotFound && location.pathname !== canonicalListPath;

    useEffect(() => {
        if (typeof window === 'undefined' || !needsCanonicalRedirect) return;
        navigate(`${canonicalListPath}${location.search || ''}`, { replace: true });
    }, [canonicalListPath, location.pathname, location.search, navigate, needsCanonicalRedirect]);

    if (shouldRenderNotFound) {
        return <PageNotFound />;
    }

    if (needsCanonicalRedirect && !isServer) {
        return null;
    }

    /**
     * Return a resized thumbnail URL by inserting `_500` before the file extension.
     * Example:
     *   /img/blog/thumbnail/1736907599990.jpg -> /img/blog/thumbnail/1736907599990_500.jpg
     */
    const getScaleDownThumbnail = (url) => {
        if (!url || typeof url !== 'string') return url;

        const lastDotIndex = url.lastIndexOf('.');
        // If no extension is found, just return original
        if (lastDotIndex === -1) return url;

        const base = url.substring(0, lastDotIndex);
        const ext = url.substring(lastDotIndex); // includes the dot, e.g. ".jpg"

        return `${process.env.REACT_APP_BASE_URL}${base}_500${ext}`;
    };

    const getColor = (targetCategory, type) => {
        let color = '#FFFFFF';
        if (categoryList && categoryList.length > 0) {
            categoryList.forEach((category, index) => {
                if (category.name === targetCategory) {
                    if (type === 'bg') {
                        color = category.backgroundColor;
                    } else if (type === 'text') {
                        color = category.textColor;
                    }
                }
            });
        }
        return color;
    };

    // Generate structured data for SEO
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const pathname = canonicalListPath;
    const pageUrl = `${baseUrl}${canonicalListPath}`;
    const pageDescriptionBase = GEO_PAGE_DESCRIPTIONS.BLOG;
    const slugKey = slug ? String(slug).toLowerCase() : null;
    const hasCategoryInUrl = Boolean(slugKey && slugKey !== 'all');
    const categoryLabel =
        hasCategoryInUrl && categoryList?.length ? categoryList.find((c) => c.name?.toLowerCase() === slugKey)?.name : null;
    // Title: Blog is always the main subject; category is a decorative qualifier (same pattern for every filter).
    const categoryTitleName =
        selectedCategory !== 'all'
            ? categoryLabel ||
              (slugKey
                  ? slugKey.charAt(0).toUpperCase() + slugKey.slice(1)
                  : selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1))
            : null;
    const pageTitle =
        selectedCategory === 'all'
            ? 'Blog | Hotmob - Data-Centric Media Company'
            : `Blog - ${categoryTitleName} | Hotmob - Data-Centric Media Company`;
    const categoryLine = hasCategoryInUrl && categoryLabel ? ` Focus: ${categoryLabel}.` : '';
    const pageNumFromUrl = page != null && page !== '' ? Number(page) : null;
    const hasPageInUrl = page != null && page !== '' && typeof pageNumFromUrl === 'number' && !Number.isNaN(pageNumFromUrl);
    const pageLine = hasPageInUrl && pageNumFromUrl > 1 ? ` Page ${pageNumFromUrl} (older posts).` : '';
    const pageDescription = `${pageDescriptionBase}${categoryLine}${pageLine}`;
    const defaultImage = `${process.env.REACT_APP_BASE_URL}/img/static/icon/hotmob_logo_3X.webp`;
    const collectionItems = (postList || []).map((post) => ({
        name: post?.postTitle || 'Blog post',
        url: buildBlogDetailPath(post?.id, post?.postTitle)
    }));

    const structuredData = buildSchemaGraph([
        generateCollectionPageSchema({
            name: pageTitle,
            description: pageDescription,
            path: pathname,
            about: 'organization',
            items: collectionItems
        }),
        generateBreadcrumbSchema({
            pagePath: pathname,
            items: [
                { name: 'Home', url: null },
                { name: 'Blog', url: pathname }
            ]
        })
    ]);

    return (
        <Container
            sx={(t) => ({
                width: '100%',
                maxWidth: '1280px !important',
                margin: '0 auto !important',
                padding: '80px 40px !important',
                [t.breakpoints.down('md')]: {
                    padding: '80px 24px !important'
                }
            })}
        >
            <Helmet>
                <html lang="en" />
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <link rel="canonical" href={pageUrl} />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content={pageUrl} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:image" content={defaultImage} />
                <meta property="og:site_name" content="Hotmob" />
                <meta property="og:locale" content="en_US" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={pageDescription} />
                <meta name="twitter:image" content={defaultImage} />

                {/* Robots */}
                <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
                <meta name="googlebot" content="index, follow" />

                {/* Structured Data */}
                {renderMultipleStructuredData([structuredData])}
            </Helmet>
            <Typography
                variant="h1"
                sx={(t) => ({
                    color: '#1C1C1C',
                    fontSize: '3.75rem',
                    fontWeight: 800,
                    marginTop: 0,
                    marginBottom: '60px',
                    lineHeight: 1.167,
                    letterSpacing: '-0.01562em',
                    [t.breakpoints.down(959.95)]: {
                        fontSize: '1.5rem',
                        marginBottom: '20px'
                    }
                })}
            >
                <Box component="span">BLO</Box>
                <Box component="span" sx={{ color: '#E15C12' }}>
                    G
                </Box>
            </Typography>
            {isLoaded && (
                <Grid container spacing={2}>
                    {categoryList && (
                        <Grid item xs={12} sm={3} md={3} lg={2}>
                            <Grid container spacing={1}>
                                <Grid item xs={6} sm={12} sx={{ display: 'flex', padding: '8px' }}>
                                    <Typography
                                        variant="h5"
                                        sx={(t) => ({
                                            color: selectedCategory === 'all' ? '#1C1C1C' : '#BAB8B8',
                                            fontSize: '1.5625rem',
                                            marginTop: 0,
                                            marginBottom: '4px',
                                            fontWeight: 800,
                                            lineHeight: 1.334,
                                            letterSpacing: '0em',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                textDecoration: 'underline #E15C12'
                                            },
                                            [t.breakpoints.down(960)]: {
                                                fontSize: '1.25rem',
                                                marginBottom: '2px'
                                            }
                                        })}
                                        onClick={() => {
                                            setSelectedCategory('all');
                                            setCurrentPage(1);
                                            navigate(buildBlogListPath('all', 1));
                                        }}
                                    >
                                        ALL
                                    </Typography>
                                </Grid>
                                {categoryList.map((category, index) => (
                                    <Grid item xs={6} sm={12} sx={{ display: 'flex', padding: '8px' }}>
                                        <Typography
                                            key={index}
                                            variant="h5"
                                            sx={(t) => ({
                                                color: selectedCategory === category.name.toLowerCase() ? '#1C1C1C' : '#BAB8B8',
                                                fontSize: '1.5625rem',
                                                marginTop: 0,
                                                marginBottom: '4px',
                                                fontWeight: 800,
                                                lineHeight: 1.334,
                                                letterSpacing: '0em',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    textDecoration: 'underline #E15C12'
                                                },
                                                [t.breakpoints.down(960)]: {
                                                    fontSize: '1.25rem',
                                                    marginBottom: '2px'
                                                }
                                            })}
                                            onClick={() => {
                                                setSelectedCategory(category.name.toLowerCase());
                                                setCurrentPage(1);
                                                navigate(buildBlogListPath(category.name.toLowerCase(), 1));
                                            }}
                                        >
                                            {category.name.toUpperCase()}
                                        </Typography>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    )}
                    {postList && (
                        <Grid
                            item
                            xs={12}
                            sm={9}
                            md={9}
                            lg={10}
                            sx={(t) => ({
                                [t.breakpoints.up(600)]: {
                                    paddingLeft: '32px !important'
                                },
                                [t.breakpoints.up(1100)]: {
                                    paddingLeft: '48px !important'
                                }
                            })}
                        >
                            <Grid container spacing={4}>
                                {postList.map((post, index) => (
                                    <Grid
                                        item
                                        key={post.id}
                                        xs={12}
                                        sm={selectedCategory === 'all' && currentPage < 2 && index === 0 ? 12 : 6}
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => {
                                            // Validate post ID before navigation
                                            if (post.id && post.id !== 'null' && post.id !== 'undefined') {
                                                navigate(buildBlogDetailPath(post.id, post.postTitle));
                                            } else if (isDevelopment) {
                                                console.error('[BlogList] Cannot navigate: Invalid post ID:', post.id);
                                                console.error('[BlogList] Post object:', post);
                                            }
                                        }}
                                    >
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={selectedCategory === 'all' && currentPage < 2 && index === 0 ? 6 : 12}>
                                                <Box
                                                    component="img"
                                                    alt={`${post.postTitle} thumbnail`}
                                                    src={getScaleDownThumbnail(post.postThumbnail)}
                                                    sx={(t) => ({
                                                        width: 'auto',
                                                        height: 'auto',
                                                        maxWidth: '100%',
                                                        maxHeight: '250px',
                                                        borderRadius: '16px',
                                                        display: 'block',
                                                        margin: '0 auto',
                                                        [t.breakpoints.down(1280)]: {
                                                            maxHeight: '200px'
                                                        },
                                                        [t.breakpoints.down(1100)]: {
                                                            maxHeight: '150px'
                                                        },
                                                        [t.breakpoints.down(600)]: {
                                                            maxHeight: '200px'
                                                        }
                                                    })}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={selectedCategory === 'all' && currentPage < 2 && index === 0 ? 6 : 12}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12}>
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                                                            {post.postCategory.map((category, index) => (
                                                                <Chip
                                                                    key={index}
                                                                    label={category}
                                                                    sx={{
                                                                        fontSize: '1rem',
                                                                        fontWeight: 700,
                                                                        padding: '0 1rem',
                                                                        marginRight: '0.5rem',
                                                                        marginBottom: '0.5rem',
                                                                        background: getColor(category, 'bg'),
                                                                        color: getColor(category, 'text')
                                                                    }}
                                                                />
                                                            ))}
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <Typography
                                                            variant="h2"
                                                            sx={(t) => ({
                                                                color: '#1C1C1C',
                                                                fontSize: '1.875rem',
                                                                fontWeight: 800,
                                                                lineHeight: 1.235,
                                                                letterSpacing: '0.00735em',
                                                                [t.breakpoints.down(960)]: {
                                                                    fontSize: '1.5rem'
                                                                }
                                                            })}
                                                        >
                                                            {post.postTitle}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <Box
                                                            component="p"
                                                            sx={(t) => ({
                                                                color: '#1C1C1C',
                                                                fontSize: '1.25rem',
                                                                fontWeight: 400,
                                                                lineHeight: 1.5,
                                                                letterSpacing: '0.00938em',
                                                                marginTop: 0,
                                                                [t.breakpoints.down(960)]: {
                                                                    fontSize: '1rem'
                                                                }
                                                            })}
                                                        >
                                                            {post.postDescription}
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                ))}
                                <Grid item xs={12}>
                                    <Stack spacing={2}>
                                        <Pagination
                                            count={Math.ceil(postCount / 10)}
                                            sx={{
                                                '& .MuiPaginationItem-root': {
                                                    '&.Mui-selected': {
                                                        background: '#E15C12',
                                                        color: '#FFFFFF'
                                                    }
                                                }
                                            }}
                                            onChange={(e, val) => {
                                                setCurrentPage(val);
                                                navigate(buildBlogListPath(selectedCategory, val));
                                            }}
                                        />
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            )}
        </Container>
    );
};

// Server-side data preloading helper
export async function loadData(store, params = {}) {
    try {
        // Validate environment variable on server - only require in development
        // In production/UAT, allow API calls to proceed (API is proxied via nginx)
        if (typeof window === 'undefined' && !process.env.REACT_APP_API_SERVER) {
            if (isDevelopment) {
                console.error('[SSR] PostListPage.loadData - REACT_APP_API_SERVER is not set on server');
                console.error('[SSR] PostListPage.loadData - Cannot make API calls without API server URL');
                // Don't throw - allow page to render even if API is not configured
                return;
            }
            // In production/UAT, API is proxied via nginx, so localhost fallback is expected
            // Only log in development to avoid noise in production/UAT logs
            if (!isProduction) {
                console.log('[SSR] PostListPage.loadData - REACT_APP_API_SERVER not set, using localhost fallback (API proxied via nginx)');
            }
        }

        try {
            // Clear existing state for the post list slice and fetch categories first
            await store.dispatch(clearStore());
        } catch (error) {
            if (isDevelopment) {
                console.warn('[SSR] PostListPage.loadData - Error clearing store:', error?.message || error);
            }
            // Continue even if clearing store fails
        }

        try {
            await store.dispatch(getCategories());
        } catch (error) {
            if (isDevelopment) {
                console.error('[SSR] PostListPage.loadData - Error loading categories:', error?.message || error);
                console.error('[SSR] PostListPage.loadData - Error details:', {
                    message: error?.message,
                    response: error?.response?.status,
                    responseData: error?.response?.data,
                    baseURL: typeof window === 'undefined' ? process.env.REACT_APP_API_SERVER : 'client'
                });
            }
            // Don't throw - allow page to render even if categories fail
            return;
        }

        // determine target category id from categories and params.slug
        const state = store.getState();
        const categoryList = state.postList && state.postList.categoryList ? state.postList.categoryList : [];
        let targetCategory = 0;
        if (params && params.slug && params.slug !== 'all') {
            const slug = params.slug.toString().toLowerCase();
            const found = categoryList.find((c) => c.name && c.name.toLowerCase() === slug);
            if (found) {
                targetCategory = found.id;
            } else if (isDevelopment) {
                console.warn('[SSR] PostListPage.loadData - Category not found for slug:', slug);
            }
        }

        const pageNum = params && params.page ? Number(params.page) : 1;

        try {
            await store.dispatch(getPostList({ categoryId: targetCategory, page: pageNum }));
        } catch (error) {
            if (isDevelopment) {
                console.error('[SSR] PostListPage.loadData - Error loading post list:', error?.message || error);
                console.error('[SSR] PostListPage.loadData - Error details:', {
                    message: error?.message,
                    response: error?.response?.status,
                    responseData: error?.response?.data,
                    responseStatusText: error?.response?.statusText,
                    baseURL: typeof window === 'undefined' ? process.env.REACT_APP_API_SERVER : 'client',
                    url: `/api/post/list/${targetCategory}/${pageNum}`
                });
            }
            // Don't throw - allow page to render even if post list fails
            // This ensures the page structure is still rendered for SEO
        }
    } catch (error) {
        if (isDevelopment) {
            console.error('[SSR] PostListPage.loadData - Unexpected error:', error?.message || error);
            console.error('[SSR] PostListPage.loadData - Error stack:', error?.stack);
        }
        // Don't throw - allow page to render even if data loading fails
        // This ensures the page structure is still rendered for SEO
    }
}

// Attach loadData as a static method to the component
PostListPage.loadData = loadData;

export default PostListPage;
